import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { stageAPI } from '../services/api';
import { dateFr } from '../services/dates';
import StageCertifPanneau from './StageCertifPanneau';
import { CasesLivrables, ActionLigne } from './StageCertifCommun';
// Lot C.4a : les fenêtres d'envoi du lot 3.S — une seule pour tout le module (C32).
import StageFenetreEnvoi from './StageFenetreEnvoi';
import StageFenetrePromotion from './StageFenetrePromotion';
import { useModelesEnvoiStage } from './FenetreModeleCourriel';
// Lot C.4b : l'export CSV (E124 — « ; », BOM), commun avec l'onglet Évaluation.
import { telechargerCsv } from '../services/csvExcel';

/**
 * SUIVI DE STAGE — CERTIFICATION, le TABLEAU DE BORD (lot C.1)
 * =============================================================
 * Spec     : doc/modules/suivi_de_stage_certification.md § 1 Tableau de bord, § Statuts
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (#certif-dash)
 *
 * De haut en bas, comme la maquette : l'encart rouge des dossiers SANS CERTIFICATEUR
 * (disparaît à zéro), le choix du processus avec « Envoyer le lien à la promo », les
 * indicateurs, les filtres, le tableau (une seule action par ligne), le panneau.
 *
 * Lot C.4a : l'envoi à la promo et les actions Envoyer / Relancer / Renvoyer ouvrent la fenêtre
 * d'envoi ; « Compléter le contact » ouvre l'endroit où corriger (CB3).
 * Lot C.4b : « Exporter (N dossiers) » — une ligne par livrable des dossiers AFFICHÉS (CR13).
 *
 * ⚠️ CB1 : un processus jamais lancé montre la promotion telle qu'elle SERAIT lancée —
 * le certificateur qui serait copié, et surtout ceux qui n'en ont pas.
 */

const REGIMES = {
    initiale: { libelle: 'Formation initiale', classe: 'fi' },
    apprentis: { libelle: 'Apprentissage', classe: 'app' },
    fc: { libelle: 'Formation continue', classe: 'fc' }
};

// Maquette : les étapes « orange » sont des cartes BLANCHES au chiffre orange ; seules les alertes (rouge) et
// les terminés (vert) sont teintées.
const TONS = {
    orange: { fond: '#ffffff', bord: '#e2e8f0', texte: '#d97706' },
    rouge:  { fond: '#fef2f2', bord: '#fecaca', texte: '#b91c1c' },
    vert:   { fond: '#f0fdf4', bord: '#bbf7d0', texte: '#15803d' }
};

/** « Rapport et soutenance » → « rapport-et-soutenance » : un nom de fichier sans accent ni espace. */
const pourFichier = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

/** Un instant (UTC) → « 30/09 », à l'heure de Paris. */
const dateCourte = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' }) : '');

/** La colonne « Lien du maître » : où en est le lien, selon l'étape (maquette) — et l'alerte d'un envoi en échec (C.4a). */
function ColonneLien({ l }) {
    return (
        <td className="certif-lien">
            <ContenuLien l={l} />
            {l.alerte && <div className="certif-alerte-envoi"><i className="fa-solid fa-triangle-exclamation" /> {l.alerte}</div>}
        </td>
    );
}

function ContenuLien({ l }) {
    const { liens } = l;
    if (l.statut === 'termine') return <>certifié le {dateCourte(liens.termine_le)}</>;
    if (l.statut === 'correction_en_cours') return <>décision le {dateCourte(liens.decision_le)}</>;
    if (l.statut === 'envoi_maitre_echoue') {
        return (
            <span style={{ color: '#b91c1c' }}>
                Dépôt validé le {dateCourte(liens.depot_valide_le)}
                <div style={{ fontSize: 10 }}><i className="fa-solid fa-triangle-exclamation" /> envoi au maître échoué</div>
            </span>
        );
    }
    if (l.statut === 'depot_valide') {
        return <>Dépôt validé le {dateCourte(liens.depot_valide_le)}<div style={{ fontSize: 10, color: '#64748b' }}>envoi au maître automatique</div></>;
    }
    if (l.statut === 'certification_en_cours' && liens.maitre) {
        return (
            <>
                envoyé le {dateCourte(liens.maitre.envoye_le)}
                {liens.maitre.ouvert_le
                    ? <div className="ouvert"><i className="fa-solid fa-eye" /> ouvert le {dateCourte(liens.maitre.ouvert_le)}</div>
                    : <div className="jamais"><i className="fa-solid fa-magnifying-glass" /> jamais ouvert</div>}
            </>
        );
    }
    if (l.statut === 'depot_en_cours' && liens.etudiant) {
        return (
            <span style={{ color: '#64748b' }}>
                —
                <div style={{ fontSize: 10 }}>
                    {liens.etudiant.ouvert_le
                        ? `lien étudiant ouvert le ${dateCourte(liens.etudiant.ouvert_le)}`
                        : `lien étudiant envoyé le ${dateCourte(liens.etudiant.envoye_le)}, jamais ouvert`}
                </div>
            </span>
        );
    }
    return <span style={{ color: '#64748b' }}>—</span>;
}

// Lot L1a (E169) : `processusFixe` = la vignette ouverte — ce processus seulement, sans le menu de choix.
// Lot L1b : `vueEnsemble` (onglet « Tableau de bord des certifications ») — s'ouvre sur « Tous les processus » :
// chaque ligne dit son processus, ses gestes visent CE processus ; envoi à une promotion et export : un processus choisi.
// L1b : `rechercheInitiale` — ouvert depuis la recherche du module, la liste arrive filtrée sur l'étudiant.
function StageCertifTableauBord({ departementId, anneeId, modifiable, onAller, processusFixe = null, vueEnsemble = false, rechercheInitiale = '' }) {
    const [donnees, setDonnees] = useState(null);
    const [processusId, setProcessusId] = useState(processusFixe || (vueEnsemble ? 'tous' : null));
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur] = useState(null);
    const [recherche, setRecherche] = useState(rechercheInitiale);
    const [regime, setRegime] = useState('');
    const [controle, setControle] = useState('');
    const [masquerTermines, setMasquerTermines] = useState(true);
    const [seulementSans, setSeulementSans] = useState(false);
    const [ouverte, setOuverte] = useState(null);           // la ligne dont le panneau est ouvert
    // Lot C.4a — la fenêtre d'envoi ouverte ({ mode: 'individuel' | 'promotion', brouillon }) et son refus éventuel.
    const [envoi, setEnvoi] = useState(null);
    const [erreurEnvoi, setErreurEnvoi] = useState(null);
    const [versionPanneau, setVersionPanneau] = useState(0);
    const [exportEnCours, setExportEnCours] = useState(false);

    // ⚠️ Aucun rappel de parent dans ces dépendances (leçon du lot 1.3 bis).
    const recharger = useCallback(async () => {
        if (!departementId) return;
        setChargement(true); setErreur(null);
        try {
            const r = await stageAPI.getCertifTableau(departementId, anneeId, processusId);
            setDonnees(r.data);
        } catch (e) {
            setErreur(e.response?.data?.error || e.message || 'Lecture du tableau de bord impossible');
        } finally { setChargement(false); }
    }, [departementId, anneeId, processusId]);
    useEffect(() => { recharger(); }, [recharger]);

    const t = donnees?.tableau;
    const p = t?.processus;
    const tous = p?.id === 'tous';
    /** Le processus d'une ligne : le sien en vue d'ensemble, sinon celui affiché. */
    const procDe = (l) => (tous && l?.processus ? l.processus : p);
    const lignes = useMemo(() => {
        const q = recherche.trim().toLowerCase();
        return (t?.lignes || []).filter((l) => {
            // CF6 : « sans certificateur » = le maître reste inconnu, même si l'étudiant a reçu son lien.
            if (seulementSans) return l.maitre_inconnu;
            if (masquerTermines && l.statut === 'termine') return false;
            if (regime && l.regime !== regime) return false;
            if (controle === 'controlees' && !(l.declarations.n > 0 && l.declarations.controlees === l.declarations.n)) return false;
            if (controle === 'non_controlees' && !(l.declarations.n > l.declarations.controlees)) return false;
            if (q) {
                const texte = [l.etudiant.nom, l.etudiant.prenom, l.etudiant.code_etudiant, l.certificateur?.nom, l.certificateur?.entreprise]
                    .filter(Boolean).join(' ').toLowerCase();
                if (!texte.includes(q)) return false;
            }
            return true;
        });
    }, [t, recherche, regime, controle, masquerTermines, seulementSans]);

    // Le panneau ouvert suit la ligne RELUE (après un envoi, son statut a changé).
    useEffect(() => {
        if (!ouverte || !t) return;
        const nouvelle = t.lignes.find((l) => l.cle === ouverte.cle || (ouverte.etudiant && l.etudiant.id === ouverte.etudiant.id));
        if (nouvelle && nouvelle !== ouverte) setOuverte(nouvelle);
    }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Les appels de la Certification, passés aux fenêtres du lot 3.S (C32 : une seule fenêtre). */
    // L1b : en vue d'ensemble, le lancement vise le processus de la LIGNE (retenu à l'ouverture de la fenêtre).
    const idProcessus = envoi?.processusId || p?.id;
    const apiIndividuel = useMemo(() => ({ envoyer: stageAPI.envoyerCertif, essai: stageAPI.essaiCertif }), []);
    const apiLancement = useMemo(() => ({
        lancer: (dep, data) => stageAPI.lancerCertif(dep, idProcessus, data),
        etat: stageAPI.etatLancementCertif
    }), [idProcessus]);
    // Lot MC (2026-10-28) : « Modèles de courriel… » — la copie du modèle de CE processus de certification ; après un
    // enregistrement, le brouillon est refait par ses routes de LECTURE (GET), qui n'écrivent rien.
    const { onModeles, fenetre } = useModelesEnvoiStage({
        departementId, typeMail: envoi?.brouillon?.type_mail,
        certifProcessusId: envoi?.brouillon?.certif_processus_id || envoi?.brouillon?.processus_id || envoi?.processusId || null,
        rePreparer: async () => (envoi?.mode === 'promotion'
            ? (await stageAPI.brouillonLancementCertif(departementId, envoi.processusId)).data
            : (await stageAPI.brouillonEnvoiCertif(departementId, envoi.brouillon.type, envoi.brouillon.dossier_id)).data)
    });

    /** CB3 : le contact d'un apprenti se complète dans le registre Alternance, celui d'un FI dans son dossier de stage. */
    const completer = (l) => onAller(['apprentis', 'fc'].includes(l.regime) ? 'alternance' : 'stagiaires', l.etudiant.nom);

    /**
     * Lot C.4a — l'action d'une ligne. Le PREMIER lien passe toujours par le lancement (il crée le dossier
     * et fige le processus) : « Envoyer le lien » ouvre la fenêtre de la promotion, cochée sur cet étudiant.
     */
    const agir = async (l, code) => {
        setErreurEnvoi(null);
        if (code === 'completer') { completer(l); return; }
        try {
            if (code === 'envoyer' || code === 'promotion') {
                const procId = procDe(l).id;
                const r = await stageAPI.brouillonLancementCertif(departementId, procId);
                const b = r.data;
                if (code === 'envoyer') b.etudiants = b.etudiants.map((x) => ({ ...x, coche: x.coche && x.dossier_id === l.etudiant.id }));
                setEnvoi({ mode: 'promotion', brouillon: b, processusId: procId });
            } else {
                const type = code === 'relancer_etudiant' ? 'certif_etudiant' : 'certif_maitre';
                const r = await stageAPI.brouillonEnvoiCertif(departementId, type, l.dossier_id);
                setEnvoi({ mode: 'individuel', brouillon: r.data });
            }
        } catch (e) {
            setErreurEnvoi({ message: e.response?.data?.error || "La fenêtre d'envoi n'a pas pu s'ouvrir", detail: e.response?.data?.detail || null });
        }
    };
    /**
     * CR13 — l'export suit l'écran : le serveur rend TOUTES les lignes du processus, on garde celles
     * qui sont AFFICHÉES (filtres et « Masquer les terminés » compris). Une ligne par livrable (CR14).
     */
    const exporter = async () => {
        setExportEnCours(true); setErreurEnvoi(null);
        try {
            const r = await stageAPI.exportCertif(departementId, p.id);
            const visibles = new Set(lignes.map((l) => l.cle));
            const corps = r.data.lignes.filter((x) => visibles.has(x.cle)).map((x) => x.valeurs);
            const jour = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });
            telechargerCsv(`certification_${pourFichier(p.formation_nom)}_${pourFichier(p.nom)}_${jour}.csv`, [r.data.colonnes, ...corps]);
        } catch (e) {
            setErreurEnvoi({ message: e.response?.data?.error || "L'export n'a pas pu être préparé", detail: null });
        } finally { setExportEnCours(false); }
    };

    const apresEnvoi = (fait) => { if (fait) { recharger(); setVersionPanneau((v) => v + 1); } };

    if (chargement && !donnees) return <div className="stg-panneau-attente"><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture du tableau de bord…</div>;
    if (erreur && !donnees) return <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>;
    if (!t) {
        return (
            <div className="stg-attente">
                <i className="fa-solid fa-diagram-project" />
                <div className="stg-attente-titre">Aucun processus cette année</div>
                <div>Créez d'abord un processus dans <b>Paramétrage → Processus</b> : il apparaîtra ici, avec sa promotion.</div>
            </div>
        );
    }

    const ind = t.indicateurs;
    // Ceux qui attendent leur PREMIER lien : « à envoyer » et, CF6, « sans certificateur ».
    const enAttente = t.lignes.filter((l) => ['a_envoyer', 'sans_certificateur'].includes(l.statut)).length;
    const total = ind.total;
    const CARTES = [
        { cle: 'a_envoyer', libelle: ['Lien', 'à envoyer'], picto: '📧', valeur: ind.a_envoyer, ton: 'orange' },
        { cle: 'depot', libelle: ['Dépôt', 'en cours'], picto: '⏳', valeur: ind.depot_en_cours, ton: 'orange' },
        { cle: 'echoue', libelle: ['Envois', 'échoués'], picto: '⚠️', valeur: ind.envoi_maitre_echoue, ton: 'rouge',
          titre: "Le lien part au maître tout seul, dès que l'étudiant valide. Cette case ne compte que les envois qui ont échoué." },
        { cle: 'certif', libelle: ['Certification', 'en cours'], picto: '🖊️', valeur: ind.certification_en_cours, ton: 'orange',
          detail: `🔎 ${ind.jamais_ouverts} jamais ouvert${ind.jamais_ouverts > 1 ? 's' : ''} · 👁️ ${ind.ouverts} ouvert${ind.ouverts > 1 ? 's' : ''}` },
        { cle: 'correction', libelle: ['Correction', 'en cours'], picto: '✏️', valeur: ind.correction_en_cours, ton: 'rouge' },
        { cle: 'termine', libelle: ['Dossiers', 'terminés'], picto: '✅', valeur: ind.termine, ton: 'vert', separe: true }
    ];

    return (
        <>
            {/* Les trous d'abord : disparaît à zéro. */}
            {ind.sans_certificateur > 0 && (
                <div className="certif-trous">
                    <i className="fa-solid fa-triangle-exclamation" />
                    <span>
                        <b>{ind.sans_certificateur} dossier{ind.sans_certificateur > 1 ? 's' : ''} sans certificateur</b> — aucun maître
                        connu (fiche de liaison non validée, registre Alternance vide ou adresse manquante). L'étudiant reçoit quand même son lien de dépôt ; le maître ne sera prévenu qu'une fois le contact complété.
                    </span>
                    <button type="button" className="btn btn-outline btn-sm" style={{ borderColor: '#fecaca', color: '#b91c1c' }}
                            onClick={() => setSeulementSans((v) => !v)}>
                        {seulementSans ? 'Voir tous les dossiers' : `Voir ${ind.sans_certificateur > 1 ? `les ${ind.sans_certificateur} dossiers` : 'le dossier'}`}
                    </button>
                </div>
            )}

            {/* C30 : c'est ICI qu'on envoie — le choix du processus et l'envoi collectif. */}
            <div className="certif-choix-processus">
                <i className="fa-solid fa-diagram-project" style={{ color: 'var(--stg-vert-d)' }} />
                <label htmlFor="certif-processus" style={{ fontSize: 12, fontWeight: 600 }}>Processus</label>
                {processusFixe
                    ? <b style={{ fontSize: 12 }}>{p.formation_nom} — {p.nom}</b>
                    : (
                <select id="certif-processus" className="input-sm stg-filtre" style={{ minWidth: 260 }}
                        value={p.id} onChange={(e) => { setProcessusId(e.target.value === 'tous' ? 'tous' : Number(e.target.value)); setOuverte(null); setSeulementSans(false); }}>
                    {vueEnsemble && <option value="tous">Tous les processus</option>}
                    {donnees.processus.map((x) => <option key={x.id} value={x.id}>{x.formation_nom} — {x.nom}</option>)}
                </select>
                    )}
                {tous ? (
                    <span className="certif-processus-dates">
                        {p.nb} processus · chaque ligne dit le sien — l'envoi à une promotion se fait en choisissant SON processus (ou dans sa vignette)
                    </span>
                ) : (
                <span className="certif-processus-dates">
                    {p.periode_debut && p.periode_fin ? `période du ${dateFr(p.periode_debut)} au ${dateFr(p.periode_fin)}` : 'période non saisie'}
                    {p.date_limite_certification ? ` · certification avant le ${dateFr(p.date_limite_certification)}` : ''}
                    {p.fige_le
                        ? <> · <i className="fa-solid fa-lock" /> figé le {dateCourte(p.fige_le)}</>
                        : ' · jamais lancé (aperçu de la promotion)'}
                </span>
                )}
                {!tous && (
                <button type="button" className="btn btn-green btn-sm" style={{ marginLeft: 'auto' }}
                        disabled={!modifiable || !enAttente}
                        title={!modifiable ? 'Lecture seule : votre profil ne permet pas de modifier ce module.'
                            : enAttente ? "La fenêtre d'envoi : un message personnel par étudiant, l'aperçu chiffré, l'essai ; au 1er envoi, le processus est figé."
                            : 'Tous les étudiants ont reçu leur lien.'}
                        onClick={() => agir(null, 'promotion')}>
                    <i className="fa-solid fa-paper-plane" /> Envoyer le lien à la promo ({enAttente})
                </button>
                )}
            </div>
            {erreurEnvoi && (
                <div className="stg-encart stg-encart-erreur">
                    <i className="fa-solid fa-circle-exclamation" />
                    <span><b>{erreurEnvoi.message}</b>{erreurEnvoi.detail ? <> — {erreurEnvoi.detail}</> : null}</span>
                </div>
            )}

            {/* Les indicateurs — même vocabulaire que le pipeline du tableau de bord stagiaire. */}
            <div className="stg-pipeline-titre"><i className="fa-solid fa-rotate" /> Avancement du processus</div>
            <div className="stg-pipeline">
                {CARTES.map((c, i) => {
                    const ton = TONS[c.ton];
                    return (
                        <React.Fragment key={c.cle}>
                            {c.separe ? <div className="stg-pipeline-sep" /> : i > 0 && <div className="stg-pipeline-fleche">→</div>}
                            <div className="stg-indicateur" style={{ background: ton.fond, borderColor: ton.bord }} title={c.titre}>
                                <div className="stg-indicateur-lib" style={{ color: ton.texte }}>
                                    <span className="stg-indicateur-picto">{c.picto}</span> {c.libelle[0]}<br />{c.libelle[1]}
                                </div>
                                <div className="stg-indicateur-val" style={{ color: ton.texte }}>{c.valeur}<span>/{total}</span></div>
                                {c.detail && <div style={{ fontSize: 9, color: ton.texte, marginTop: 3 }}>{c.detail}</div>}
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>

            <div className="stg-filtres">
                <div className="stg-recherche">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input placeholder="Rechercher un étudiant, un maître, une entreprise…" value={recherche}
                           onChange={(e) => setRecherche(e.target.value)} aria-label="Rechercher" />
                </div>
                <select className="input-sm stg-filtre" value={regime} onChange={(e) => setRegime(e.target.value)} aria-label="Régime">
                    <option value="">Tous les régimes</option>
                    {Object.entries(REGIMES).map(([k, v]) => <option key={k} value={k}>{v.libelle}</option>)}
                </select>
                <select className="input-sm stg-filtre" value={controle} onChange={(e) => setControle(e.target.value)}
                        title="Voir où en est le contrôle aléatoire — l'application ne désigne personne (C27)" aria-label="Déclarations hors entreprise">
                    <option value="">Déclarations hors entreprise : toutes</option>
                    <option value="controlees">… contrôlées</option>
                    <option value="non_controlees">… non contrôlées</option>
                </select>
                <label className="stg-case">
                    <input type="checkbox" checked={masquerTermines} onChange={(e) => setMasquerTermines(e.target.checked)} /> Masquer les terminés
                </label>
                <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }}
                        disabled={!lignes.length || exportEnCours || tous} onClick={exporter}
                        title={tous ? "Choisissez un processus pour exporter : l'export suit les livrables d'UN processus." : `CSV lisible par Excel : une ligne par livrable des ${lignes.length} dossier${lignes.length > 1 ? 's' : ''} affiché${lignes.length > 1 ? 's' : ''} — les filtres comptent${masquerTermines ? ' (les terminés sont masqués : décochez « Masquer les terminés » pour les inclure)' : ''}.`}>
                    <i className={'fa-solid ' + (exportEnCours ? 'fa-circle-notch stg-tourne' : 'fa-download')} /> Exporter ({lignes.length} dossier{lignes.length > 1 ? 's' : ''})
                </button>
            </div>

            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

            <div className="card">
                <div className="stg-table-defile">
                    <table className="certif-table">
                        <thead>
                            <tr>
                                <th>Étudiant</th><th>Régime</th><th>Certificateur</th><th>Documents</th><th>Lien du maître</th><th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lignes.length === 0 && (
                                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#64748b', padding: '24px 14px' }}>
                                    {(t.lignes || []).length === 0 ? "Aucun étudiant inscrit dans cette formation cette année." : 'Aucun dossier ne correspond à ces filtres.'}
                                </td></tr>
                            )}
                            {lignes.map((l) => {
                                const r = REGIMES[l.regime] || REGIMES.initiale;
                                const c = l.certificateur || {};
                                const fond = ['sans_certificateur', 'envoi_maitre_echoue'].includes(l.statut) || l.maitre_inconnu || l.alerte ? '#fef2f2'
                                    : l.retard_jours > 0 ? '#fff7ed' : undefined;
                                return (
                                    <tr key={l.cle} className="stg-ligne" style={{ cursor: 'pointer', background: fond }}
                                        onClick={() => setOuverte(l)} title="Cliquer pour ouvrir le dossier">
                                        <td>
                                            <strong>{l.etudiant.nom} {l.etudiant.prenom}</strong>
                                            {l.retard_jours > 0
                                                ? <div style={{ fontSize: 10, color: '#d97706', marginTop: 2 }}><i className="fa-solid fa-clock" /> En retard — {l.retard_jours} j</div>
                                                : <div style={{ fontSize: 10, color: '#64748b' }}>{/* E171 (fin) : la formation de l'ÉTUDIANT (un processus peut en viser plusieurs). */}
                                                    {tous ? `${l.formation?.nom || l.processus.formation_nom} — ${l.processus.nom}` : (l.formation?.nom || t.processus.formation_nom)}</div>}
                                            {tous && l.retard_jours > 0 && <div style={{ fontSize: 10, color: '#64748b' }}>{l.formation?.nom || l.processus.formation_nom} — {l.processus.nom}</div>}
                                        </td>
                                        <td><span className={'certif-regime ' + r.classe}>{r.libelle}</span></td>
                                        {c.manque && !c.nom ? (
                                            <td style={{ color: '#b91c1c', fontSize: 12 }}>
                                                <i className="fa-solid fa-triangle-exclamation" /> Aucun contact connu
                                                <div style={{ fontSize: 10, color: '#64748b' }}>{c.manque}</div>
                                            </td>
                                        ) : (
                                            <td style={{ fontSize: 12 }}>
                                                {c.nom}
                                                <div style={{ fontSize: 10, color: c.manque ? '#b91c1c' : '#64748b' }}>
                                                    {c.manque ? <><i className="fa-solid fa-triangle-exclamation" /> {c.manque}</> : c.entreprise}
                                                </div>
                                                {/* CF6 : trouvé depuis le lancement — copié dans le dossier au prochain envoi au maître. */}
                                                {c.a_copier && !c.manque && <div style={{ fontSize: 10, color: '#92400e' }}>trouvé depuis : copié au prochain envoi au maître</div>}
                                            </td>
                                        )}
                                        <td><CasesLivrables cases={l.cases} resume={l.resume} /></td>
                                        <ColonneLien l={l} />
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <ActionLigne action={l.action} statut={l.statut} modifiable={modifiable} onAction={(code) => agir(l, code)} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="certif-legende">
                    Cases des documents : <span className="certif-doc attendu">·</span> attendu ·{' '}
                    <span className="certif-doc depose">·</span> déposé · <span className="certif-doc certifie">·</span> certifié ·{' '}
                    <span className="certif-doc non-certifie">·</span> ne peut pas être certifié · <span className="certif-doc hors">H</span> déclaré hors entreprise.
                    {' '}Une seule action par ligne : elle vise l'étudiant ou le maître, selon où en est le dossier.
                </div>
            </div>

            {ouverte && (
                <StageCertifPanneau key={versionPanneau} ligne={ouverte} processus={procDe(ouverte)} departementId={departementId} modifiable={modifiable}
                                    onFermer={() => setOuverte(null)} onAction={(code) => agir(ouverte, code)}
                                    onChange={recharger} />
            )}

            {/* Lot C.4a — les fenêtres d'envoi : l'assistante relit et modifie avant d'envoyer. */}
            {envoi?.mode === 'individuel' && (
                <StageFenetreEnvoi brouillon={envoi.brouillon} departementId={departementId} api={apiIndividuel}
                                   onFermer={() => setEnvoi(null)} onTermine={apresEnvoi} onModeles={onModeles} />
            )}
            {envoi?.mode === 'promotion' && (
                <StageFenetrePromotion brouillon={envoi.brouillon} departementId={departementId} api={apiLancement}
                                       onFermer={() => setEnvoi(null)} onTermine={apresEnvoi} onModeles={onModeles} />
            )}
            {fenetre}
        </>
    );
}

export default StageCertifTableauBord;
